import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  Navigation2,
  CheckCircle2,
  Clock,
  DollarSign,
  AlertTriangle,
  ClipboardCheck,
  MapPin,
  Calendar,
  ChevronRight,
  ShieldCheck,
  Phone,
  Fuel
} from 'lucide-react';
import { useAreaMotorista } from '../../contexts/AreaMotoristaContext';
import { formatDateBR } from '../../utils/dateUtils';

export const AreaMotoristaDashboard: React.FC = () => {
  const {
    currentMotorista,
    isAgregado,
    stats,
    ordensServico,
    viagemAtiva,
    setViagemAtiva,
    iniciarViagem,
    isLoading
  } = useAreaMotorista();

  const navigate = useNavigate();
  const hojeIso = new Date().toISOString().split('T')[0];
  const hojeLocal = new Date().toLocaleDateString('sv-SE');

  // Filtra exclusivamente as viagens com atendimento agendado para o dia de hoje
  const viagensHoje = ordensServico.filter(os => {
    if (os.status_id === 3) return false; // Exclui canceladas
    const dtAtendimento = os.data_atendimento ? os.data_atendimento.split('T')[0] : (os.created_at ? os.created_at.split('T')[0] : '');
    return dtAtendimento === hojeIso || dtAtendimento === hojeLocal || os.status_id === 2;
  });

  const handleStartTrip = async (osId: number) => {
    const os = ordensServico.find(o => o.id === osId);
    if (os) {
      setViagemAtiva(os);
      await iniciarViagem(osId);
      navigate('/area-motorista/execucao');
    }
  };

  return (
    <div className="space-y-6 text-left">
      
      {/* Driver Welcome Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 border border-slate-800 p-5 sm:p-6 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 relative z-10">
          <div className="space-y-1">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-600/20 border border-red-500/30 text-red-400 text-xs font-bold uppercase tracking-wider">
              <Truck className="w-3.5 h-3.5" />
              <span>Painel Operacional do Motorista</span>
            </div>
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight">
              Olá, {currentMotorista?.nome?.split(' ')[0] || 'Motorista'}!
            </h1>
            <p className="text-xs text-slate-400">
              {currentMotorista?.placa
                ? `Veículo vinculado: ${currentMotorista.placa} • ${currentMotorista.tipo_vinculo || (isAgregado ? 'Agregado' : 'Motorista Próprio (CLT)')}`
                : 'Tenha uma excelente e segura jornada de trabalho!'}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/area-motorista/checklist')}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 text-xs font-bold flex items-center gap-2 transition shadow"
            >
              <ClipboardCheck className="w-4 h-4 text-amber-400" />
              <span>Fazer Vistoria</span>
            </button>
            <button
              onClick={() => navigate('/area-motorista/viagens')}
              className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold flex items-center gap-2 transition shadow-lg shadow-red-600/30"
            >
              <Navigation2 className="w-4 h-4" />
              <span>Ver Rota</span>
            </button>
          </div>
        </div>

        {/* Ambient background glow */}
        <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-red-600/10 rounded-full blur-3xl pointer-events-none" />
      </div>

      {/* Primary KPI Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        
        {/* Viagens Hoje */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Viagens Hoje</span>
            <div className="w-7 h-7 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center">
              <Calendar className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-white">{stats.viagensHoje}</p>
          <span className="text-[10px] text-slate-500">Agendadas para a data atual</span>
        </div>

        {/* Em Andamento */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Em Andamento</span>
            <div className="w-7 h-7 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-amber-400">{stats.viagensEmAndamento}</p>
          <span className="text-[10px] text-slate-500">Em rota ou em atendimento</span>
        </div>

        {/* Concluídas */}
        <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Concluídas</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <p className="text-2xl font-black text-emerald-400">{stats.viagensConcluidas}</p>
          <span className="text-[10px] text-slate-500">Com assinatura registrada</span>
        </div>

        {/* Repasses do Mês (Apenas Agregados) vs Status Operacional (Motoristas CLT) */}
        {isAgregado ? (
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Ganhos Acumulados</span>
              <div className="w-7 h-7 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center">
                <DollarSign className="w-4 h-4" />
              </div>
            </div>
            <p className="text-xl font-black text-purple-300">
              {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stats.totalGanhosMes)}
            </p>
            <span className="text-[10px] text-slate-500">Repasses do mês (Agregado)</span>
          </div>
        ) : (
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Jornada Ativa</span>
              <div className="w-7 h-7 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center">
                <ShieldCheck className="w-4 h-4" />
              </div>
            </div>
            <p className="text-base font-black text-emerald-400">Em Conformidade</p>
            <span className="text-[10px] text-slate-500">Motorista CLT / Frota Própria</span>
          </div>
        )}
      </div>

      {/* Active Trip in Progress (Highlight Card) */}
      {viagemAtiva ? (
        <div className="rounded-2xl border-2 border-red-500/60 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-5 shadow-2xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500 animate-ping" />
              <span className="text-xs font-black text-white uppercase tracking-wider">
                Viagem em Execução Agora • OS #{viagemAtiva.id}
              </span>
            </div>
            <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/30">
              Em Rota
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div className="space-y-1.5 p-3 rounded-xl bg-slate-900/80 border border-slate-800">
              <div className="flex items-center gap-1.5 text-blue-400 font-bold">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span>Local de Coleta / Retirada:</span>
              </div>
              <p className="text-slate-200 font-medium pl-5">{viagemAtiva.retirada || 'Endereço de Coleta'}</p>
            </div>

            <div className="space-y-1.5 p-3 rounded-xl bg-slate-900/80 border border-slate-800">
              <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
                <MapPin className="w-3.5 h-3.5 shrink-0" />
                <span>Local de Entrega / Destino:</span>
              </div>
              <p className="text-slate-200 font-medium pl-5">{viagemAtiva.entrega || 'Endereço de Entrega'}</p>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="text-xs text-slate-400">
              <span>Carga: </span>
              <strong className="text-slate-200">{viagemAtiva.descricao || 'Carga Padrão'}</strong>
            </div>
            <button
              onClick={() => navigate('/area-motorista/execucao')}
              className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold text-xs flex items-center gap-2 shadow-lg shadow-red-600/30 transition"
            >
              <span>Abrir Navegação e Concluir</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      ) : (
        /* Se não há viagem ativa, mostra a próxima agendada */
        viagensHoje.length > 0 && (
          <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 sm:p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-white flex items-center gap-2">
                <Navigation2 className="w-4 h-4 text-red-500" />
                <span>Próxima Viagem Agendada</span>
              </h2>
              <span className="text-xs text-slate-400">Hoje</span>
            </div>

            {(() => {
              const proxima = viagensHoje.find(os => os.status_id !== 4 && !os.assinatura) || viagensHoje[0];
              return (
                <div className="p-4 rounded-xl bg-slate-900 border border-slate-800 space-y-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-white">OS #{proxima.id}</span>
                    {isAgregado ? (
                      <span className="text-emerald-400 font-mono font-bold">
                        Repasse: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(proxima.repasse) || 0)}
                      </span>
                    ) : (
                      <span className="text-blue-400 font-medium">
                        Carga Atribuída
                      </span>
                    )}
                  </div>

                  <div className="text-xs space-y-1 text-slate-300">
                    <p className="line-clamp-1">📍 <strong>De:</strong> {proxima.retirada || 'Origem'}</p>
                    <p className="line-clamp-1">🏁 <strong>Para:</strong> {proxima.entrega || 'Destino'}</p>
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800/80">
                    <button
                      onClick={() => handleStartTrip(proxima.id)}
                      className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold flex items-center gap-1.5 shadow transition"
                    >
                      <Navigation2 className="w-3.5 h-3.5" />
                      <span>Iniciar Viagem</span>
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )
      )}

      {/* Agenda de Viagens Recentes */}
      <div className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4 sm:p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-red-500" />
            <h2 className="text-sm font-bold text-white">Minhas Viagens Recentes</h2>
          </div>
          <button
            onClick={() => navigate('/area-motorista/viagens')}
            className="text-xs text-red-400 hover:text-red-300 font-semibold flex items-center gap-1"
          >
            <span>Ver todas</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-xs text-slate-500">Carregando viagens...</div>
        ) : ordensServico.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-500 space-y-2">
            <Truck className="w-8 h-8 mx-auto text-slate-600 stroke-[1.5]" />
            <p>Nenhuma ordem de serviço atribuída no momento.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {ordensServico.slice(0, 4).map((os) => {
              const isConcluida = os.status_id === 4 || !!os.assinatura;
              return (
                <div
                  key={os.id}
                  onClick={() => {
                    setViagemAtiva(os);
                    navigate('/area-motorista/execucao');
                  }}
                  className="p-3.5 rounded-xl bg-slate-900/80 hover:bg-slate-900 border border-slate-800/80 hover:border-slate-700 cursor-pointer flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs transition"
                >
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-white">OS #{os.id}</span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          isConcluida
                            ? 'bg-emerald-950 text-emerald-400 border border-emerald-800'
                            : 'bg-amber-950 text-amber-400 border border-amber-800'
                        }`}
                      >
                        {isConcluida ? 'Concluída' : 'Em Aberto / Rota'}
                      </span>
                      {os.data_atendimento && (
                        <span className="text-slate-500 text-[11px]">
                          {formatDateBR(os.data_atendimento)}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-300 truncate font-medium">
                      🏁 {os.entrega || 'Destino não especificado'}
                    </p>
                  </div>

                  <div className="flex items-center justify-between sm:justify-end gap-3 shrink-0">
                    {isAgregado && (
                      <span className="text-xs font-mono font-bold text-emerald-400">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(os.repasse) || 0)}
                      </span>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Action Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => navigate('/area-motorista/checklist')}
          className="p-4 rounded-2xl bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-left space-y-1.5 transition group"
        >
          <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-400 flex items-center justify-center group-hover:scale-110 transition">
            <ClipboardCheck className="w-4 h-4" />
          </div>
          <h3 className="text-xs font-bold text-white">Checklist Diário</h3>
          <p className="text-[11px] text-slate-400">Vistoria de pneus, nível de óleo, freios e segurança.</p>
        </button>

        {isAgregado ? (
          <button
            onClick={() => navigate('/area-motorista/extrato')}
            className="p-4 rounded-2xl bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-left space-y-1.5 transition group"
          >
            <div className="w-8 h-8 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center group-hover:scale-110 transition">
              <DollarSign className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Extrato de Ganhos</h3>
            <p className="text-[11px] text-slate-400">Consulte os repasses, diárias e valores recebidos.</p>
          </button>
        ) : (
          <button
            onClick={() => navigate('/area-motorista/viagens')}
            className="p-4 rounded-2xl bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-left space-y-1.5 transition group"
          >
            <div className="w-8 h-8 rounded-xl bg-blue-500/10 text-blue-400 flex items-center justify-center group-hover:scale-110 transition">
              <Truck className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Minhas Viagens</h3>
            <p className="text-[11px] text-slate-400">Consulte todas as rotas e coletas atribuídas.</p>
          </button>
        )}

        <a
          href="https://wa.me/5511999999999?text=Ol%C3%A1%2C%20preciso%20de%20suporte%20operacional%20Voope"
          target="_blank"
          rel="noopener noreferrer"
          className="p-4 rounded-2xl bg-slate-950/60 hover:bg-slate-900 border border-slate-800 text-left space-y-1.5 transition group block"
        >
          <div className="w-8 h-8 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center group-hover:scale-110 transition">
            <Phone className="w-4 h-4" />
          </div>
          <h3 className="text-xs font-bold text-white">Suporte Voope (WhatsApp)</h3>
          <p className="text-[11px] text-slate-400">Fale diretamente com a equipe de tráfego e operações.</p>
        </a>
      </div>

    </div>
  );
};
